import { useEffect, useState } from 'react';
import { apiurl } from '../common/constants';

/**
 * TawkTo Chat Widget Component
 * 
 * Usage:
 * 1. With props: <TawkTo propertyId="xxx" widgetId="yyy" />
 * 2. Dynamic from API: <TawkTo /> (fetches from /api/settings/tawkto)
 */
export default function TawkTo({ propertyId: propPropertyId, widgetId: propWidgetId }) {
  const [config, setConfig] = useState({
    propertyId: propPropertyId || null,
    widgetId: propWidgetId || null,
    enabled: true
  });
  const [loaded, setLoaded] = useState(false);

  // Fetch config from API if not provided via props
  useEffect(() => {
    const fetchTawkConfig = async () => {
      if (propPropertyId && propWidgetId) {
        // Use props if provided
        setConfig({
          propertyId: propPropertyId,
          widgetId: propWidgetId,
          enabled: true
        });
        return;
      }

      try {
        const response = await fetch(`${apiurl}/settings/tawkto`);
        if (response.ok) {
          const data = await response.json();
          // Settings are stored as { key: 'tawkto', value: { propertyId, widgetId, enabled } }
          if (data?.data?.value?.propertyId && data?.data?.value?.widgetId) {
            setConfig({
              propertyId: data.data.value.propertyId,
              widgetId: data.data.value.widgetId,
              enabled: data.data.value.enabled !== false
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch Tawk.to config:', error);
      }
    };

    fetchTawkConfig();
  }, [propPropertyId, propWidgetId]);

  // Load Tawk.to script
  useEffect(() => {
    if (loaded || !config.propertyId || !config.widgetId || !config.enabled) {
      return;
    }

    // Initialize Tawk_API
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // Create and inject script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://embed.tawk.to/${config.propertyId}/${config.widgetId}`;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    script.id = 'tawkto-script';

    // Insert script
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    setLoaded(true);

    // Cleanup on unmount
    return () => {
      const existingScript = document.getElementById('tawkto-script');
      if (existingScript) {
        existingScript.remove();
      }
      // Remove Tawk widget
      const tawkWidget = document.querySelector('[class*="tawk"]');
      if (tawkWidget) {
        tawkWidget.remove();
      }
      delete window.Tawk_API;
      delete window.Tawk_LoadStart;
    };
  }, [config, loaded]);

  // This component doesn't render anything visible
  return null;
}

// Helper functions to control Tawk.to widget programmatically
export const TawkToAPI = {
  // Maximize/open the chat widget
  maximize: () => {
    if (window.Tawk_API?.maximize) {
      window.Tawk_API.maximize();
    }
  },
  
  // Minimize the chat widget
  minimize: () => {
    if (window.Tawk_API?.minimize) {
      window.Tawk_API.minimize();
    }
  },
  
  // Hide the chat widget
  hide: () => {
    if (window.Tawk_API?.hideWidget) {
      window.Tawk_API.hideWidget();
    }
  },
  
  // Show the chat widget
  show: () => {
    if (window.Tawk_API?.showWidget) {
      window.Tawk_API.showWidget();
    }
  },
  
  // Toggle the widget
  toggle: () => {
    if (window.Tawk_API?.toggle) {
      window.Tawk_API.toggle();
    }
  },
  
  // Set visitor attributes
  setAttributes: (attributes) => {
    if (window.Tawk_API?.setAttributes) {
      window.Tawk_API.setAttributes(attributes);
    }
  },
  
  // Set visitor name and email
  setVisitor: (name, email) => {
    if (window.Tawk_API?.setAttributes) {
      window.Tawk_API.setAttributes({
        name: name,
        email: email
      });
    }
  }
};
